// @flow

import type Map from '../map';
import urls from '../../util/urls';
import {getJSON, ResourceType} from '../../util/ajax';
import {extend} from '../../util/util';
import {Event, ErrorEvent} from '../../util/evented';
import type {Options} from './content_base';
import {ContentBase} from './content_base';
import config from '../../util/config';

/**
 * Traffic Incident Content Layer
 * @param options
 */
export default class TrafficIncident extends ContentBase {
    constructor(options: Options) {
        const opt = extend({layerId: 'trafficincidents'}, options);
        super(opt);
    }

    _getContent() {
        const urlTemplate = `${urls.BASE_URL}/styles/v5/${this._map.getRegion()}/${this._getStyleName()}/${this._options.layerId}?key=${config.API_KEY || ''}`;
        const url = this._map._requestManager.normalizeStyleURL(urlTemplate);
        const request = this._map._requestManager.transformRequest(url, ResourceType.Style);
        this._layerNames = [];
        /**
         * Fired when a traffic incident layer is loading.
         *
         * @event trafficincidentloading
         * @memberof Map
         * @instance
         * @type {Object}
         * @property {Event} event traffic incident layer is start loading.
         * @example
         * myMap.on('trafficincidentloading', function(e) {
         *   // your code goes here.
         * });

         */
        this._map.fire(new Event('trafficincidentloading'));
        getJSON(request, (error: ?Error, json: ?Object) => {
            if (error) {
                this._map.fire(new ErrorEvent(error));
            } else if (json) {
                for (const id in json.sources) {
                    if (!this._map.style.getSource(id)) {
                        this._map.style.addSource(id, json.sources[id]);
                    }
                }
                if (json.layers.length) {
                    json.layers.forEach((layer) => { // only one layer
                        const newLayerId = `${this._options.layerId}-${layer.id}`;
                        this._layerNames.push(newLayerId);
                        layer.id = newLayerId;
                        this._map.addLayer(layer);
                    });
                    this.setVisibility(this._isVisible);
                }
                /**
                 * Fired when a traffic incident layer is loaded.
                 *
                 * @event trafficincident
                 * @memberof Map
                 * @instance
                 * @type {Object}
                 * @property {Event} traffic incident layer has been returned.
                 */
                this._map.fire(new Event('trafficincident'));
            }
        });
    }

    /**
     * Add the layer to a map.
     * @param {Map} map The map.
     * @returns {TrafficIncident} `this`
     */
    addTo(map: Map) {
        return super.addTo(map);
    }

    /**
     * Remove the layer from a map.
     * @returns {TrafficIncident} `this`
     */
    remove() {
        return super.remove();
    }

    /**
     * Returns true if the layer is enabled. Visibility of layer content may be affected by other factors, such as zoom level.
     * @returns {boolean} A boolean indicating the visibility of the layer.
     */
    isVisible() {
        return super.isVisible();
    }

    /**
     * Set the visibility of the layer. Visibility of layer content may be affected by other factors, such as zoom level.
     * @param {boolean} isVisible A boolean indicating the visibility of the layer.
     * @returns {TrafficIncident} `this`
     */
    setVisibility(isVisible: boolean) {
        return super.setVisibility(isVisible);
    }

    /**
     * Toggle the visibility of the layer. Visibility of layer content may be affected by other factors, such as zoom level.
     * @returns {TrafficIncident} `this`
     */
    toggleVisibility() {
        return super.toggleVisibility();
    }
}
